import { useTimeoutFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import type { ComputedRef, Ref } from 'vue';
import {
    computed,
    ref,
    watch,
} from 'vue';
import type { IBetSlipCashOut } from '@/interface/IBetSlipCashOut';
import type { IOdds } from '@/interface/IOdds';
import type { ISingleTicket, ITicketError, Ticket } from '@/interface/ITicket';
import {
    BetPageType,
    BetSlipCtaAction,
    BetSlipState,
    ParlayStakeError,
    PlaceOrderStatus,
    SingleModeBalanceError,
    SingleModeBetSlipTab,
    StatementDetailStatus,
    TicketType,
} from '@/interface/enum';
import type { IPlaceParlayResult } from '@/libraries/apis/ticketApi';
import { placeParlayTicket as placeParlayTicketApi } from '@/libraries/apis/ticketApi';
import { getPlaceBetErrorMessage } from '@/libraries/betSlip/betSlipErrorMessage';
import {
    createParlayTicketOrderState,
    groupTicketsByParlayOrder,
    sortTicketsByParlayOrder,
    syncParlayTicketOrder,
} from '@/libraries/betSlip/parlayTicketOrder';
import type { IPlacedOrderErrorContext } from '@/libraries/betSlip/singleModeBetSlipErrorMessage';
import {
    PlacedOrderFailureAction,
    buildGeneralFailureError,
    resolvePlacedOrderFailurePolicy,
    toSingleModeFooterMessage,
} from '@/libraries/betSlip/singleModeBetSlipErrorMessage';
import { exceedsStakeMaxLength } from '@/libraries/betSlip/stake';
import { useBetSlipBalance } from '@/libraries/composables/useBetSlipBalance';
import { useParlayCashOut } from '@/libraries/composables/useBetSlipCashOut';
import {
    createParlaySubBetSignature,
    useParlayOpenTicket,
} from '@/libraries/composables/useParlayOpenTicket';
import {
    generateComboParlayCombination,
    getComboTotalOdds,
    getTotalBetCount,
    getTotalOdds,
    getTotalPayout,
} from '@/libraries/helpers/comboParlayHelper';
import { platform } from '@/libraries/helpers/platformHelper';
import { getStakeInputMultiplier } from '@/libraries/helpers/stakeInputHelper';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { isMainMarket } from '@/libraries/marketGroupHelper';
import { getSportsBsiUrl } from '@/libraries/url';
import { sleep } from '@/libraries/utils/utils';
import { useAccountStore } from '@/stores/accountStore';
import { useBetCountStore } from '@/stores/betCountStore';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

const DEFAULT_MIN_PARLAY_SUB_BET_COUNT = 3;

export const TWO_SUB_BET_PARLAY_COUNT = 2;
const MAX_INDIVIDUAL_COMBO_FOLD_SUB_BET_COUNT = 8;
const AFTER_PARLAY_PLACE_REFRESH_DELAY_MS = 1000;

export interface IResolveParlayCtaActionInput {
    isBalanceBelowMinimum: boolean;
    shouldShowDeposit: boolean;
    shouldUpdateStakeForBalance: boolean;
    stakeError: ParlayStakeError | null;
    hasUnacceptedOddsChange: boolean;
    isAcceptAnyOddsEnabled: boolean;
}

export function resolveParlayCtaAction(
    input: IResolveParlayCtaActionInput,
): BetSlipCtaAction {
    if (input.isBalanceBelowMinimum) {
        return input.shouldShowDeposit
            ? BetSlipCtaAction.Deposit
            : BetSlipCtaAction.PlaceBet;
    }
    if (input.shouldUpdateStakeForBalance || input.stakeError !== null) {
        return BetSlipCtaAction.UpdateStake;
    }
    if (!input.isAcceptAnyOddsEnabled && input.hasUnacceptedOddsChange) {
        return BetSlipCtaAction.AcceptChange;
    }
    return BetSlipCtaAction.PlaceBet;
}

export function resolveParlayBetPage(isComboParlay: boolean, isBigBetSlip: boolean): BetPageType {
    if (isComboParlay) {
        return BetPageType.SingleModeComboParlay_Big;
    }
    return isBigBetSlip
        ? BetPageType.SingleModeParlay_Big
        : BetPageType.SingleModeParlay_Medium;
}

export interface IComboParlayOption {
    fold: number;
    betCount: number;
    odds: number;
    payout: number;
    isSelected: boolean;
    canCashOut: boolean;
}

export interface IParlayInSingleDeps {
    tickets: ComputedRef<Ticket[]>;
    activeTab: Ref<SingleModeBetSlipTab>;
    isPlacingBet: ComputedRef<boolean>;
    isTicketListVisible: ComputedRef<boolean>;
    removeAllTickets: () => void;
    markTicketClosed: (key: string, messageKey: string) => void;
}

export function isParlayEligibleTicket(ticket: Ticket): ticket is ISingleTicket {
    return ticket.type === TicketType.Single && ticket.hasMixParlay;
}

export function useParlayInSingle(deps: IParlayInSingleDeps) {
    const {
        tickets,
        activeTab,
        isPlacingBet,
        isTicketListVisible,
        removeAllTickets,
        markTicketClosed,
    } = deps;
    const featureToggleStore = useFeatureToggleStore();
    const { isSupportMpTwoSubBets } = featureToggleStore;
    const {
        isShowDepositInTicketEnabled,
        placeBetDelayTime,
        cashOutSubBetMinCount,
    } = storeToRefs(featureToggleStore);
    const rootStore = useRootStore();
    const {
        setTopScreenToast,
        setShowAcceptAnyOddsBanner,
        setFailedPlaceBetShowAaoBanner,
    } = rootStore;
    const { brandName } = storeToRefs(rootStore);
    const { refreshBetCount } = useBetCountStore();
    const { betSlipState } = storeToRefs(useBetSlipStore());
    const accountStore = useAccountStore();
    const { customerInfo, balance } = storeToRefs(accountStore);
    const { setBalance, formatMoney } = accountStore;
    const stakeInputMultiplier = computed(() => getStakeInputMultiplier(customerInfo.value.currency));

    const isParlayTabActive = computed(() => activeTab.value === SingleModeBetSlipTab.Parlay);

    const ticketOrderState = ref(createParlayTicketOrderState());
    watch(tickets, (currentTickets) => {
        ticketOrderState.value = syncParlayTicketOrder(ticketOrderState.value, currentTickets);
    }, { immediate: true, flush: 'sync' });
    const orderedParlayTickets = computed(() => sortTicketsByParlayOrder(
        ticketOrderState.value,
        tickets.value,
    ));
    const parlayEligibleTickets = computed(() => tickets.value.filter(isParlayEligibleTicket));
    const parlayIneligibleTickets = computed(() => tickets.value
        .filter(ticket => !isParlayEligibleTicket(ticket)));
    const conflictedParlayKeys = computed<Set<string>>(() => {
        const keysByEvent = new Map<number, string[]>();
        parlayEligibleTickets.value.forEach((ticket) => {
            const keys = keysByEvent.get(ticket.eventId) ?? [];
            keys.push(ticket.key);
            keysByEvent.set(ticket.eventId, keys);
        });
        const conflicted = new Set<string>();
        keysByEvent.forEach((keys) => {
            if (keys.length > 1) {
                keys.forEach(key => conflicted.add(key));
            }
        });
        return conflicted;
    });
    const parlayTicketGroups = computed(() => groupTicketsByParlayOrder(
        ticketOrderState.value,
        tickets.value,
    ).map(group => ({
        ...group,
        isConflicted: group.tickets.some(ticket => conflictedParlayKeys.value.has(ticket.key)),
    })));
    const parlaySubBets = computed(() => parlayEligibleTickets.value
        .filter(ticket => !conflictedParlayKeys.value.has(ticket.key)));
    const parlayOddsByKey = ref<Record<string, IOdds>>({});
    watch(parlayEligibleTickets, (currentTickets) => {
        const activeKeys = new Set(currentTickets.map(ticket => ticket.key));
        parlayOddsByKey.value = Object.fromEntries(
            Object.entries(parlayOddsByKey.value)
                .filter(([key]) => activeKeys.has(key)),
        );
    });

    function updateParlayOdds(key: string, odds: IOdds): boolean {
        const ticket = parlayEligibleTickets.value.find(candidate => candidate.key === key);
        if (!ticket || !odds.priceOption.some(priceOption => priceOption.option === ticket.option)) {
            return false;
        }
        parlayOddsByKey.value = {
            ...parlayOddsByKey.value,
            [key]: odds,
        };
        return true;
    }
    const isTwoSubBetParlaySupported = computed(() => parlaySubBets.value.length > 0
        && parlaySubBets.value.every(ticket => isSupportMpTwoSubBets(ticket.event.leagueId)));
    const isValid2MpSubBets = computed(() => (
        parlaySubBets.value.length === TWO_SUB_BET_PARLAY_COUNT
        && isTwoSubBetParlaySupported.value
    ));
    const minParlaySubBetCount = computed(() => (
        isTwoSubBetParlaySupported.value
            ? TWO_SUB_BET_PARLAY_COUNT
            : DEFAULT_MIN_PARLAY_SUB_BET_COUNT
    ));
    const missingParlaySubBetCount = computed(() => Math.max(
        0,
        minParlaySubBetCount.value - parlaySubBets.value.length,
    ));
    const isParlaySubBetCountValid = computed(() => (
        parlaySubBets.value.length >= minParlaySubBetCount.value
    ));
    const hasIneligibleParlayTickets = computed(() => parlayIneligibleTickets.value.length > 0);
    const hasConflictedParlayTickets = computed(() => conflictedParlayKeys.value.size > 0);
    const shouldShowParlayInsufficientMessage = computed(() => (
        missingParlaySubBetCount.value > 0
        && !hasIneligibleParlayTickets.value
        && !hasConflictedParlayTickets.value
    ));
    const {
        openTicketError: parlayOpenTicketError,
        displayedSnapshot: displayedParlayOpenSnapshot,
        uid: parlayUid,
        minBet: parlayMinBet,
        maxBet: parlayMaxBet,
        isOpening: isOpeningParlay,
        isOpenBlocked: isParlayOpenBlocked,
        subBetResults: parlayOpenSubBetResults,
        lastKnownSubBetResults: lastKnownParlaySubBetResults,
        subBetErrors: parlaySubBetErrors,
        subBetChangeErrors: parlaySubBetChangeErrors,
        totalOdds: parlayTotalOdds,
        isOddsChangeAccepted: isParlayOddsChangeAccepted,
        openTicket: openParlayTicket,
        acceptOddsChanges: acceptParlayOddsChanges,
        resetOpenState: resetParlayOpenState,
    } = useParlayOpenTicket({
        subBets: parlaySubBets,
        isActive: isParlayTabActive,
        canOpen: isParlaySubBetCountValid,
        setBalance,
        markSubBetClosed: markTicketClosed,
    });
    const parlayDisplaySubBetResults = computed(() => (
        isParlaySubBetCountValid.value
            ? {
                ...lastKnownParlaySubBetResults.value,
                ...Object.fromEntries(
                    Object.entries(parlayOpenSubBetResults.value)
                        .filter(([, subBet]) => subBet.isSuccess),
                ),
            }
            : parlayOpenSubBetResults.value
    ));
    const hasBlockedParlaySelections = computed(() => (
        hasIneligibleParlayTickets.value
        || hasConflictedParlayTickets.value
        || Object.keys(parlaySubBetErrors.value).length > 0
    ));

    const parlayStake = ref(0);
    const isReopeningRevealedParlay = ref(false);
    const selectedCombo = ref<number[]>([]);
    const comboFolds = computed<number[]>(() => {
        const subBetCount = parlaySubBets.value.length;
        if (!isParlaySubBetCountValid.value) {
            return [];
        }
        if (subBetCount > MAX_INDIVIDUAL_COMBO_FOLD_SUB_BET_COUNT) {
            return [subBetCount];
        }
        return Array.from({ length: subBetCount - 1 }, (_, index) => index + 2);
    });
    // Both the MixParlay Odds API and Open Parlay return display-ready Euro prices.
    // Price Style remains unknown before Open Parlay, but neither source needs conversion.
    const euroParlaySubBetPrices = computed<(number | null)[]>(() => parlaySubBets.value.map((ticket) => {
        const parlaySubBetResult = parlayDisplaySubBetResults.value[ticket.key];
        if (parlaySubBetResult?.isSuccess === true) {
            return parlaySubBetResult.price;
        }
        return parlayOddsByKey.value[ticket.key]?.priceOption
            .find(priceOption => priceOption.option === ticket.option)?.price
            ?? null;
    }));
    const knownEuroParlaySubBetPrices = computed(() => (
        euroParlaySubBetPrices.value.filter((price): price is number => price !== null)
    ));
    const parlayDisplayOdds = computed(() => {
        if (hasBlockedParlaySelections.value) {
            return 0;
        }
        if (knownEuroParlaySubBetPrices.value.length === 0) {
            return 0;
        }
        return getTotalOdds(knownEuroParlaySubBetPrices.value);
    });
    const hasPendingParlayPrice = computed(() => euroParlaySubBetPrices.value.includes(null));
    const liveComboDisplay = computed(() => {
        const folds = new Set([...comboFolds.value, ...selectedCombo.value]);
        return {
            folds: comboFolds.value,
            odds: Object.fromEntries([...folds].map(fold => [
                fold,
                generateComboParlayCombination(knownEuroParlaySubBetPrices.value, fold),
            ])) as Record<number, number[][]>,
        };
    });
    // Combinations computed from an incomplete price set would misstate every
    // fold, so hold the last complete state until the pending MP quote arrives.
    const heldComboDisplay = ref(liveComboDisplay.value);
    watch(liveComboDisplay, (nextComboDisplay) => {
        if (!hasPendingParlayPrice.value) {
            heldComboDisplay.value = nextComboDisplay;
        }
    });
    const comboOdds = computed(() => heldComboDisplay.value.odds);
    const comboTotalOdds = computed(() => getComboTotalOdds(comboOdds.value));
    const isShowCombo = computed(() => isParlaySubBetCountValid.value);
    const parlaySubBetCashOuts = computed<IBetSlipCashOut[]>(() => parlaySubBets.value.map(ticket => ({
        sportType: ticket.event.sportType,
        isMainMarket: isMainMarket(ticket.odds.marketGroup.id),
        isLive: ticket.odds.isLive,
        leagueId: ticket.event.leagueId,
        marketType: ticket.odds.marketType,
    })));
    const { isDisplayCashOutIcon: isParlayCashOutSupported } = useParlayCashOut(parlaySubBetCashOuts);
    const comboParlayOptions = computed<IComboParlayOption[]>(() => heldComboDisplay.value.folds.map((fold) => {
        const totalOdds = comboTotalOdds.value[fold] ?? 0;
        return {
            fold,
            betCount: comboOdds.value[fold]?.length ?? 0,
            odds: totalOdds,
            payout: totalOdds * parlayStake.value,
            isSelected: selectedCombo.value.includes(fold),
            canCashOut: isParlayCashOutSupported.value && fold >= cashOutSubBetMinCount.value,
        };
    }));

    function addOrRemoveSelectedCombo(fold: number) {
        selectedCombo.value = selectedCombo.value.includes(fold)
            ? selectedCombo.value.filter(selectedFold => selectedFold !== fold)
            : [...selectedCombo.value, fold];
    }

    function resetSelectedCombo() {
        const subBetCount = parlaySubBets.value.length;
        selectedCombo.value = subBetCount >= TWO_SUB_BET_PARLAY_COUNT
            ? [subBetCount]
            : [];
    }

    function isComboParlaySelection(subBetCount: number): boolean {
        return !(selectedCombo.value.length === 1 && selectedCombo.value.includes(subBetCount));
    }

    const parlaySubBetSignature = computed(() => createParlaySubBetSignature(parlaySubBets.value));
    // AC(TCSP-8071): adding or removing selections resets the combo selection
    // to only the full accumulator.
    watch(parlaySubBetSignature, () => {
        resetSelectedCombo();
    }, { immediate: true });

    const parlayStakeError = computed<ParlayStakeError | null>(() => {
        if (parlayStake.value === 0 || parlayMinBet.value === 0) {
            return null;
        }
        if (parlayStake.value < parlayMinBet.value) {
            return ParlayStakeError.BelowMinimum;
        }
        if (parlayStake.value > parlayMaxBet.value) {
            return ParlayStakeError.AboveMaximum;
        }
        return null;
    });
    const parlayTotalBetCount = computed(() => getTotalBetCount(selectedCombo.value, comboOdds.value));
    const parlayTotalStake = computed(() => parlayTotalBetCount.value * parlayStake.value);
    const parlayMinimumRequiredBalance = computed(() => (
        parlayTotalBetCount.value > 0 && parlayMinBet.value > 0
            ? parlayTotalBetCount.value * parlayMinBet.value
            : null
    ));
    const affordableParlayStake = computed(() => (
        balance.value === null || parlayTotalBetCount.value === 0
            ? null
            : Math.floor(balance.value / parlayTotalBetCount.value)
    ));
    const parlayStakeUpperLimit = computed(() => (
        affordableParlayStake.value === null
            ? parlayMaxBet.value
            : Math.min(parlayMaxBet.value, affordableParlayStake.value)
    ));
    const {
        isBalanceBelowMinimumRequired: isParlayBalanceBelowMinimum,
        isRequiredBalanceExceeded: isParlayRequiredBalanceExceeded,
        isRequiredBalanceAdjustable: isParlayStakeBalanceAdjustable,
        shouldShowDeposit: shouldShowParlayDeposit,
    } = useBetSlipBalance({
        isActive: isParlayTabActive,
        isEnabled: computed(() => true),
        hasEnteredStake: computed(() => parlayStake.value > 0),
        requiredBalance: parlayTotalStake,
        minimumRequiredBalance: parlayMinimumRequiredBalance,
        affordableStake: affordableParlayStake,
        minimumStake: computed(() => (parlayMinBet.value > 0 ? parlayMinBet.value : null)),
        balance,
        isShowDepositInTicketEnabled,
        playerType: computed(() => customerInfo.value.playerType),
    });
    const parlayBalanceError = computed<SingleModeBalanceError | null>(() => {
        if (isParlayBalanceBelowMinimum.value) {
            return SingleModeBalanceError.BelowMinimumStake;
        }
        if (isParlayRequiredBalanceExceeded.value) {
            return SingleModeBalanceError.SingleStakeExceedsBalance;
        }
        return null;
    });
    const parlayMaxPayout = computed(() => {
        if (hasBlockedParlaySelections.value) {
            return 0;
        }
        if (selectedCombo.value.length > 0) {
            return getTotalPayout(selectedCombo.value, comboTotalOdds.value, parlayStake.value);
        }
        return isShowCombo.value ? 0 : parlayDisplayOdds.value * parlayStake.value;
    });

    function setParlayStake(stake: number) {
        if (exceedsStakeMaxLength(stake)) {
            return;
        }
        parlayStake.value = stake;
    }

    function appendParlayStakeDigits(digits: string) {
        const digitPlaceMultiplier = 10 ** digits.length;
        const nextStake = parlayStake.value * digitPlaceMultiplier
            + Number(digits) * stakeInputMultiplier.value;
        setParlayStake(nextStake);
    }

    function addParlayStakeAmount(amount: number) {
        setParlayStake(parlayStake.value + amount);
    }

    function setMaxParlayStake() {
        setParlayStake(parlayStakeUpperLimit.value);
    }

    function backspaceParlayStake() {
        const nextStake = Math.floor(parlayStake.value / (10 * stakeInputMultiplier.value))
            * stakeInputMultiplier.value;
        setParlayStake(nextStake);
    }

    function updateParlayStakeForLimits() {
        if (parlayStake.value < parlayMinBet.value) {
            setParlayStake(parlayMinBet.value);
            return;
        }
        if (parlayStake.value > parlayStakeUpperLimit.value) {
            setParlayStake(parlayStakeUpperLimit.value);
        }
    }

    function resetParlayStake() {
        parlayStake.value = 0;
    }

    const isParlayStakePadOpen = ref(false);

    function openParlayStakePad() {
        isParlayStakePadOpen.value = true;
    }

    function closeParlayStakePad() {
        isParlayStakePadOpen.value = false;
    }

    const isPlacingParlay = ref(false);
    const afterParlayPlaceRefreshTimer = useTimeoutFn(() => {
        refreshBetCount(false);
        platform.updateBalance();
        window.eventEmitter.emit('refreshBetList');
    }, AFTER_PARLAY_PLACE_REFRESH_DELAY_MS, { immediate: false });

    const hasStakeAndUnacceptedParlayOddsChange = computed(() => (
        parlayStake.value > 0 && !isParlayOddsChangeAccepted.value
    ));
    const parlayCtaAction = computed(() => resolveParlayCtaAction({
        isBalanceBelowMinimum: isParlayBalanceBelowMinimum.value,
        shouldShowDeposit: shouldShowParlayDeposit.value,
        shouldUpdateStakeForBalance: isParlayStakeBalanceAdjustable.value,
        stakeError: parlayStakeError.value,
        hasUnacceptedOddsChange: hasStakeAndUnacceptedParlayOddsChange.value,
        isAcceptAnyOddsEnabled: customerInfo.value.isAcceptAnyOdds,
    }));
    // The displayed snapshot survives a background re-open of the same selection
    // set (live odds pushes re-open every few seconds), so Place Bet stays
    // available and places with the last complete snapshot — the signature and
    // completeness checks below still block the first open and any open after the
    // selection set changed.
    const isParlayPlaceable = computed(() => {
        const snapshot = displayedParlayOpenSnapshot.value;
        return !isPlacingBet.value
            && !isPlacingParlay.value
            && !isReopeningRevealedParlay.value
            && isParlayTabActive.value
            && !isParlayOpenBlocked.value
            && isParlaySubBetCountValid.value
            && !hasIneligibleParlayTickets.value
            && !hasConflictedParlayTickets.value
            && snapshot !== null
            && snapshot.signature === createParlaySubBetSignature(parlaySubBets.value)
            && snapshot.isComplete
            && (customerInfo.value.isAcceptAnyOdds || snapshot.isOddsChangeAccepted)
            && selectedCombo.value.length > 0
            && parlayStake.value > 0
            && parlayStakeError.value === null
            && !isParlayBalanceBelowMinimum.value
            && !isParlayRequiredBalanceExceeded.value;
    });
    const isParlayCtaDisabled = computed(() => {
        if (isPlacingBet.value || isPlacingParlay.value) {
            return true;
        }
        if (parlayCtaAction.value === BetSlipCtaAction.Deposit) {
            return false;
        }
        if (isParlayBalanceBelowMinimum.value) {
            return true;
        }
        if (isParlayRequiredBalanceExceeded.value && !isParlayStakeBalanceAdjustable.value) {
            return true;
        }
        return parlayCtaAction.value === BetSlipCtaAction.PlaceBet
            && !isParlayPlaceable.value;
    });

    async function placeParlay() {
        const snapshot = displayedParlayOpenSnapshot.value;
        if (!isParlayPlaceable.value || snapshot === null) {
            return;
        }
        closeParlayStakePad();
        isPlacingParlay.value = true;
        try {
            const [result] = await Promise.all([
                placeParlayTicketApi({
                    subBets: snapshot.subBets,
                    subBetResults: snapshot.subBetResults,
                    stake: parlayStake.value,
                    uid: snapshot.uid,
                    selectedCombo: [...selectedCombo.value],
                    betPage: resolveParlayBetPage(
                        isComboParlaySelection(snapshot.subBets.length),
                        betSlipState.value === BetSlipState.Big,
                    ),
                }),
                sleep(placeBetDelayTime.value),
            ]);
            if (result.isSuccess) {
                handleParlayPlaceSuccess(result);
            } else {
                routeParlayPlaceFailure(result);
            }
        } catch (error) {
            console.error('Failed to place Parlay in Single mode', error);
            setFailedPlaceBetShowAaoBanner(true);
            endParlayAttemptWithFailure(buildGeneralFailureError(
                { message: '', placeOrderStatus: PlaceOrderStatus.GeneralFailure },
            ));
        } finally {
            isPlacingParlay.value = false;
        }
    }

    async function handleParlayCtaClick() {
        if (isParlayCtaDisabled.value) {
            return;
        }
        switch (parlayCtaAction.value) {
            case BetSlipCtaAction.UpdateStake:
                updateParlayStakeForLimits();
                return;
            case BetSlipCtaAction.AcceptChange:
                await acceptParlayOddsChanges();
                return;
            case BetSlipCtaAction.Deposit:
                return;
            case BetSlipCtaAction.PlaceBet:
                await placeParlay();
                return;
            default: {
                const unsupportedAction: never = parlayCtaAction.value;
                throw new Error(`Unsupported Parlay CTA action: ${unsupportedAction}`);
            }
        }
    }

    function handleParlayPlaceSuccess(result: IPlaceParlayResult) {
        setTopScreenToast({
            status: 'placeBetSuccess',
            messageKey: result.orderStatus === StatementDetailStatus.Running
                ? 'betSlip_placeBetConfirmed'
                : 'betSlip_placeBetBeingProcessed',
            messagePosition: 'position-bottom',
        });
        setShowAcceptAnyOddsBanner(true);
        afterParlayPlaceRefreshTimer.start();
        removeAllTickets();
    }

    function placedOrderErrorContext(): IPlacedOrderErrorContext {
        return {
            playerType: customerInfo.value.playerType,
            brandName: brandName.value,
        };
    }

    function routeParlayPlaceFailure(result: IPlaceParlayResult) {
        setFailedPlaceBetShowAaoBanner(true);
        if (result.placeOrderStatus === PlaceOrderStatus.MPMaxAccumulatedStakeForSubEventExceed) {
            endParlayAttemptWithFailure(buildParlaySubEventLimitError(result));
            return;
        }
        const { action, buildError } = resolvePlacedOrderFailurePolicy(result.placeOrderStatus);
        if (action === PlacedOrderFailureAction.RedirectToBsi) {
            window.location.href = getSportsBsiUrl();
            return;
        }
        endParlayAttemptWithFailure(buildError(result, placedOrderErrorContext()));
    }

    function endParlayAttemptWithFailure(error: ITicketError) {
        setTopScreenToast({
            status: 'error',
            ...toSingleModeFooterMessage(error, formatMoney),
            messagePosition: 'position-bottom',
        });
        removeAllTickets();
    }

    function buildParlaySubEventLimitError(result: IPlaceParlayResult): ITicketError {
        const subBet = parlaySubBets.value.find(ticket => ticket.eventId === result.extraInfo);
        const subEvent = subBet
            ? `${getTranslated(subBet.event.homeTeam?.teamName)} -VS- ${getTranslated(subBet.event.awayTeam?.teamName)}`
            : '';
        return {
            messageKey: result.message || getPlaceBetErrorMessage(result.placeOrderStatus),
            messageData: { subEvent },
            status: result.placeOrderStatus,
        };
    }

    const parlayRequestSignature = computed(() => (
        isParlayTabActive.value
            ? createParlaySubBetSignature(parlaySubBets.value)
            : null
    ));

    watch(
        [parlayRequestSignature, isTicketListVisible],
        ([signature, isVisible], [previousSignature, wasVisible]) => {
            if (signature === null) {
                if (previousSignature !== null) {
                    resetParlayOpenState();
                    closeParlayStakePad();
                }
                return;
            }
            if (!isParlaySubBetCountValid.value) {
                resetParlayOpenState();
                return;
            }
            const didSelectionSetChange = signature !== previousSignature;
            const didTicketListBecomeVisible = isVisible && !wasVisible;
            if (didTicketListBecomeVisible && !didSelectionSetChange) {
                // The hidden list carried no subscription updates, so this
                // catch-up re-open blocks placing until the prices are fresh.
                isReopeningRevealedParlay.value = true;
                openParlayTicket().finally(() => {
                    isReopeningRevealedParlay.value = false;
                });
                return;
            }
            if (didSelectionSetChange) {
                openParlayTicket();
            }
        },
    );

    return {
        orderedParlayTickets,
        parlayTicketGroups,
        parlayEligibleTickets,
        parlayIneligibleTickets,
        conflictedParlayKeys,
        parlaySubBets,
        parlayOddsByKey,
        minParlaySubBetCount,
        missingParlaySubBetCount,
        isParlaySubBetCountValid,
        isValid2MpSubBets,
        hasIneligibleParlayTickets,
        hasConflictedParlayTickets,
        hasBlockedParlaySelections,
        shouldShowParlayInsufficientMessage,
        parlayUid,
        parlayMinBet,
        parlayMaxBet,
        isOpeningParlay,
        isParlayOpenBlocked,
        parlayOpenTicketError,
        parlayOpenSubBetResults,
        parlayDisplaySubBetResults,
        parlaySubBetErrors,
        parlaySubBetChangeErrors,
        parlayTotalOdds,
        parlayDisplayOdds,
        parlayStake,
        parlayStakeError,
        selectedCombo,
        isShowCombo,
        comboParlayOptions,
        parlayTotalBetCount,
        parlayTotalStake,
        isParlayBalanceBelowMinimum,
        isParlayRequiredBalanceExceeded,
        isParlayStakeBalanceAdjustable,
        shouldShowParlayDeposit,
        parlayBalanceError,
        parlayMaxPayout,
        isParlayOddsChangeAccepted,
        isParlayStakePadOpen,
        isPlacingParlay,
        parlayCtaAction,
        isParlayCtaDisabled,
        setParlayStake,
        appendParlayStakeDigits,
        addParlayStakeAmount,
        setMaxParlayStake,
        backspaceParlayStake,
        updateParlayStakeForLimits,
        resetParlayStake,
        addOrRemoveSelectedCombo,
        resetSelectedCombo,
        openParlayStakePad,
        updateParlayOdds,
        closeParlayStakePad,
        acceptParlayOddsChanges,
        openParlayTicket,
        handleParlayCtaClick,
    };
}
